import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, Users, Calendar, Music, Plus, Trash2, Edit3, Save, X, Image, AlertTriangle,
  Upload, ExternalLink, Facebook, Instagram, Youtube, Loader2, Check, ArrowRight,
  Headphones, Mail, Play, Pause, Inbox, ShieldCheck, ShieldAlert, MapPin,
  Download, Briefcase, FileText, Send, Sliders, Sparkles, Keyboard, Disc3
} from 'lucide-react';
import { audioBufferToWavBlob, sliceAudioBuffer, decodeAudioUrl } from '../utils/audioWav';
import AlbumPacksTab from './AlbumPacksTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    onLogin(password, (err) => setError(err));
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <Lock size={32} className="text-yellow-400 mx-auto mb-3" />
          <h2 className="text-xl font-black text-white">Admin KAZAJAH</h2>
          <p className="text-zinc-500 text-sm mt-1">Entrez le mot de passe</p>
        </div>
        <form onSubmit={handleLogin}>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white mb-3 focus:outline-none focus:border-yellow-500"
            placeholder="Mot de passe admin" />
          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
          <button type="submit" data-testid="admin-login-button"
            className="w-full py-3 bg-yellow-500 text-black font-bold rounded-xl hover:bg-yellow-400 transition-colors">
            Connexion
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function ArtistsTab({ adminPwd }) {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchArtists = useCallback(async () => {
    const res = await fetch(`${API}/artists`);
    const data = await res.json();
    setArtists(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchArtists(); }, [fetchArtists]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet artiste ?')) return;
    await fetch(`${API}/admin/artists/${id}?password=${adminPwd}`, { method: 'DELETE' });
    fetchArtists();
  };

  const handleSave = async (form, isNew = false) => {
    const formData = new FormData();
    formData.append('password', adminPwd);
    formData.append('name', form.name);
    formData.append('role', form.role || '');
    formData.append('description', form.description || '');
    formData.append('real_name', form.real_name || '');
    formData.append('featured', form.featured ? 'true' : 'false');
    formData.append('history', form.history || '');
    formData.append('socials_json', JSON.stringify(form.socials || {}));
    formData.append('videos_json', JSON.stringify(Array.isArray(form.videos) ? form.videos : []));
    if (form.imageFile) {
      formData.append('image', form.imageFile);
    } else if (form.image) {
      formData.append('image_url', form.image);
    }

    const url = isNew ? `${API}/admin/artists` : `${API}/admin/artists/${form.id}`;
    const method = isNew ? 'POST' : 'PUT';
    await fetch(url, { method, body: formData });
    setEditing(null);
    setShowAdd(false);
    fetchArtists();
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin text-yellow-400 mx-auto" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h3 className="text-lg font-bold text-white">{artists.length} Artistes</h3>
        <button onClick={() => { setEditing(null); setShowAdd(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          data-testid="add-artist-button"
          title="Ajouter un nouvel artiste"
          className="px-6 py-3.5 bg-red-500 hover:bg-red-400 text-white text-sm font-black rounded-full inline-flex items-center gap-2 shadow-lg shadow-red-500/40 cursor-pointer ring-2 ring-red-400/30 hover:ring-red-300 hover:scale-105 transition-all uppercase tracking-wider">
          <Plus size={18} /> Ajouter un nouvel artiste
        </button>
      </div>

      {(showAdd || editing) && (
        <ArtistForm
          artist={editing}
          onSave={(form) => handleSave(form, !editing)}
          onCancel={() => { setEditing(null); setShowAdd(false); }}
        />
      )}

      {/* Floating Add button — always visible while scrolling the artists list */}
      {!showAdd && !editing && (
        <button onClick={() => { setEditing(null); setShowAdd(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          data-testid="add-artist-fab"
          title="Ajouter un nouvel artiste"
          className="fixed bottom-6 right-6 z-40 px-5 py-4 bg-red-500 hover:bg-red-400 text-white text-sm font-black rounded-full inline-flex items-center gap-2 shadow-2xl shadow-red-500/50 ring-4 ring-red-400/40 hover:ring-red-300 hover:scale-105 transition-all uppercase">
          <Plus size={20} /> Nouvel artiste
        </button>
      )}

      <div className="space-y-3">
        {artists.map((a) => (
          <div key={a.id} data-testid={`admin-artist-${a.id}`}
            className="flex items-center gap-4 p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl">
            <img src={a.image || 'https://via.placeholder.com/48'} alt={a.name}
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-sm truncate">{a.name}</span>
                {a.featured && <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full font-bold">TOP</span>}
              </div>
              <p className="text-zinc-500 text-xs truncate">{a.role}</p>
              {a.socials && Object.keys(a.socials).length > 0 && (
                <div className="flex gap-1 mt-1">
                  {a.socials.facebook && <Facebook size={12} className="text-blue-400" />}
                  {a.socials.instagram && <Instagram size={12} className="text-pink-400" />}
                  {a.socials.youtube && <Youtube size={12} className="text-red-400" />}
                  {a.socials.spotify && <span className="text-green-400 text-[10px] font-bold">SP</span>}
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => setEditing(a)} data-testid={`edit-artist-${a.id}`}
                className="p-2 bg-zinc-700 rounded-lg hover:bg-zinc-600 text-zinc-300">
                <Edit3 size={14} />
              </button>
              <button onClick={() => handleDelete(a.id)} data-testid={`delete-artist-${a.id}`}
                className="p-2 bg-red-500/10 rounded-lg hover:bg-red-500/20 text-red-400">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArtistForm({ artist, onSave, onCancel }) {
  const [form, setForm] = useState({
    id: artist?.id || '',
    name: artist?.name || '',
    real_name: artist?.real_name || artist?.realName || '',
    role: artist?.role || '',
    description: artist?.description || '',
    image: artist?.image || '',
    featured: artist?.featured || false,
    socials: artist?.socials || {},
    videos: Array.isArray(artist?.videos) ? artist.videos : [],
    history: artist?.history || '',
    imageFile: null
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const nameRef = useRef(null);

  // Auto-focus the required name field when the modal opens
  useEffect(() => {
    const t = setTimeout(() => { try { nameRef.current?.focus(); } catch (_e) { /* noop */ } }, 150);
    return () => clearTimeout(t);
  }, []);

  const nameMissing = !form.name?.trim();
  const showNameError = submitAttempted && nameMissing;

  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (nameMissing) {
      try { nameRef.current?.focus(); } catch (_e) { /* noop */ }
      return;
    }
    onSave(form);
  };

  const updateSocial = (key, val) => {
    setForm(prev => ({ ...prev, socials: { ...prev.socials, [key]: val } }));
  };

  const detectPlatform = (url) => {
    const u = (url || '').toLowerCase();
    if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
    if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
    if (u.includes('soundcloud.com')) return 'soundcloud';
    if (u.includes('vimeo.com')) return 'vimeo';
    if (u.includes('spotify.com')) return 'spotify';
    if (u.includes('tiktok.com')) return 'tiktok';
    return 'other';
  };

  const addVideo = () => setForm(prev => ({ ...prev, videos: [...prev.videos, { title: '', url: '', platform: 'youtube' }] }));
  const updateVideo = (idx, key, val) => setForm(prev => ({
    ...prev,
    videos: prev.videos.map((v, i) => i === idx ? { ...v, [key]: val, ...(key === 'url' ? { platform: detectPlatform(val) } : {}) } : v)
  }));
  const removeVideo = (idx) => setForm(prev => ({ ...prev, videos: prev.videos.filter((_, i) => i !== idx) }));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onCancel}
      data-testid="artist-form-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="my-4 w-full max-w-3xl max-h-[92vh] bg-zinc-950 border border-yellow-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col">

        {/* Header (fixed at top) */}
        <div className="px-5 py-3.5 bg-gradient-to-r from-yellow-500/15 via-orange-500/10 to-transparent border-b border-zinc-800 flex items-center justify-between shrink-0">
          <div>
            <p className="text-[10px] text-yellow-400 font-bold uppercase tracking-widest">Admin · Artistes</p>
            <h4 className="text-white font-black text-base">{artist ? 'Modifier l\'artiste' : 'Nouvel artiste'}</h4>
          </div>
          <button onClick={onCancel} className="p-2 text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Scrollable content */}
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
        {/* Section 1 — IDENTITE */}
        <section>
          <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400 mb-2">1 · Identite</h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Nom de scene *</label>
              <input ref={nameRef} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg text-white text-sm focus:outline-none ${showNameError ? 'border-red-500 ring-2 ring-red-500/40 focus:border-red-400' : 'border-zinc-800 focus:border-yellow-500'}`}
                placeholder="Ex: DJHALLMANN" data-testid="artist-form-name" />
              {showNameError && (
                <p className="text-red-400 text-[11px] mt-1 font-semibold" data-testid="artist-form-name-error">
                  ⚠ Le nom de scene est obligatoire pour sauvegarder.
                </p>
              )}
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Vrai nom</label>
              <input value={form.real_name || ''} onChange={(e) => setForm({ ...form, real_name: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" placeholder="Ex: Didier Vernabel" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Role / metier</label>
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" placeholder="DJ / Rappeur / Chanteur..." />
            </div>
            <label className="flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg cursor-pointer hover:border-yellow-500/40">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="w-4 h-4 accent-yellow-500" data-testid="artist-form-featured" />
              <span className="text-zinc-300 text-sm">Top Artiste (affiche en premier sur le site)</span>
            </label>
          </div>
        </section>

        {/* Section 2 — DESCRIPTION & BIO */}
        <section>
          <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400 mb-2">2 · Description & biographie</h5>
          <div className="space-y-2">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Description courte (1 phrase)</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500 resize-none" rows={2} placeholder="Ex: Rappeur Guadeloupeen, hits LOVE et CHALÈ..." />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Biographie complete (affichee dans la modale artiste)</label>
              <textarea value={form.history} onChange={(e) => setForm({ ...form, history: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500 resize-none" rows={5} placeholder="Histoire, parcours, collaborations..." />
            </div>
          </div>
        </section>

        {/* Section 3 — PHOTO */}
        <section>
          <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400 mb-2">3 · Photo de profil</h5>
          <div className="flex gap-3 items-start">
            {(form.image && !form.imageFile) && <img src={form.image} alt="" className="w-20 h-20 rounded-lg object-cover border border-zinc-700 flex-shrink-0" />}
            {form.imageFile && <div className="w-20 h-20 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center flex-shrink-0 text-[10px] text-yellow-300 text-center px-1">{form.imageFile.name.slice(0, 18)}…</div>}
            <div className="flex-1 space-y-2">
              <input value={form.imageFile ? form.imageFile.name : (form.image || '')} onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" placeholder="https://... (URL d'image)" />
              <label className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-zinc-300 text-xs cursor-pointer">
                <Upload size={14} /> Uploader un fichier (jpg/png/webp)
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  if (e.target.files[0]) setForm({ ...form, imageFile: e.target.files[0], image: e.target.files[0].name });
                }} />
              </label>
            </div>
          </div>
        </section>

        {/* Section 4 — RESEAUX SOCIAUX */}
        <section>
          <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400 mb-2">4 · Reseaux sociaux</h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <Facebook size={16} className="text-blue-400 flex-shrink-0" />
              <input value={form.socials.facebook || ''} onChange={(e) => updateSocial('facebook', e.target.value)}
                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500" placeholder="Facebook URL" />
            </div>
            <div className="flex items-center gap-2">
              <Instagram size={16} className="text-pink-400 flex-shrink-0" />
              <input value={form.socials.instagram || ''} onChange={(e) => updateSocial('instagram', e.target.value)}
                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-pink-500" placeholder="Instagram URL" />
            </div>
            <div className="flex items-center gap-2">
              <Youtube size={16} className="text-red-400 flex-shrink-0" />
              <input value={form.socials.youtube || ''} onChange={(e) => updateSocial('youtube', e.target.value)}
                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-red-500" placeholder="YouTube channel/video URL" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-400 text-[10px] font-black flex-shrink-0 w-[16px] text-center">SP</span>
              <input value={form.socials.spotify || ''} onChange={(e) => updateSocial('spotify', e.target.value)}
                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-green-500" placeholder="Spotify URL" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400 text-[10px] font-black flex-shrink-0 w-[16px] text-center">SC</span>
              <input value={form.socials.soundcloud || ''} onChange={(e) => updateSocial('soundcloud', e.target.value)}
                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-orange-500" placeholder="SoundCloud URL" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-cyan-400 text-[10px] font-black flex-shrink-0 w-[16px] text-center">TK</span>
              <input value={form.socials.tiktok || ''} onChange={(e) => updateSocial('tiktok', e.target.value)}
                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-cyan-500" placeholder="TikTok URL" />
            </div>
          </div>
        </section>

        {/* Section 5 — VIDEOS */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400">5 · Videos (KAZAJAH TV)</h5>
            <button type="button" onClick={addVideo} data-testid="artist-form-add-video"
              className="px-3 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 rounded-lg text-[11px] font-bold hover:bg-yellow-500/30 flex items-center gap-1">
              <Plus size={11} /> Ajouter une video
            </button>
          </div>
          <p className="text-[10px] text-emerald-400 mb-2 flex items-center gap-1">
            <Check size={11} /> Chaque video ajoutee ici apparait automatiquement dans KAZAJAH TV (sauf SoundCloud/Spotify, qui restent dans la fiche artiste).
          </p>
          {form.videos.length === 0 ? (
            <p className="text-zinc-500 text-xs italic px-3 py-3 bg-zinc-900/50 rounded-lg border border-dashed border-zinc-800 text-center">
              Aucune video. Ajoute des liens YouTube, Facebook, SoundCloud, Vimeo, TikTok…
            </p>
          ) : (
            <div className="space-y-2">
              {form.videos.map((v, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-[80px_1fr_2fr_auto] items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg p-2"
                  data-testid={`artist-form-video-${idx}`}>
                  <span className={`text-[9px] font-black uppercase px-2 py-1 rounded text-center ${
                    v.platform === 'youtube' ? 'bg-red-500/20 text-red-400' :
                    v.platform === 'facebook' ? 'bg-blue-500/20 text-blue-400' :
                    v.platform === 'soundcloud' ? 'bg-orange-500/20 text-orange-400' :
                    v.platform === 'vimeo' ? 'bg-cyan-500/20 text-cyan-400' :
                    v.platform === 'spotify' ? 'bg-green-500/20 text-green-400' :
                    v.platform === 'tiktok' ? 'bg-pink-500/20 text-pink-400' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>{v.platform}</span>
                  <input value={v.title} onChange={(e) => updateVideo(idx, 'title', e.target.value)}
                    placeholder="Titre"
                    className="px-3 py-1.5 bg-black border border-zinc-800 rounded text-white text-xs focus:outline-none focus:border-yellow-500" />
                  <input value={v.url} onChange={(e) => updateVideo(idx, 'url', e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="px-3 py-1.5 bg-black border border-zinc-800 rounded text-white text-xs focus:outline-none focus:border-yellow-500"
                    data-testid={`artist-form-video-url-${idx}`} />
                  <button type="button" onClick={() => removeVideo(idx)}
                    data-testid={`artist-form-remove-video-${idx}`}
                    className="p-1.5 text-red-400 hover:bg-red-500/20 rounded justify-self-end"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </section>
        </div>

        {/* Footer actions (always visible) */}
        <div className="px-5 py-3.5 bg-zinc-950 border-t border-zinc-800 flex items-center justify-end gap-2 shrink-0">
          {submitAttempted && nameMissing && (
            <p className="mr-auto text-red-400 text-xs font-bold flex items-center gap-1" data-testid="artist-form-footer-error">
              ⚠ Renseigne le "Nom de scene" pour continuer
            </p>
          )}
          <button onClick={onCancel}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm">Annuler</button>
          <button onClick={handleSubmit} data-testid="artist-form-save"
            title={nameMissing ? 'Renseigne le Nom de scene en premier' : 'Sauvegarder cet artiste'}
            className="px-5 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-black rounded-lg text-sm flex items-center gap-2 transition-colors">
            <Save size={14} /> Sauvegarder
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function EventsTab({ adminPwd }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchEvents = useCallback(async () => {
    const res = await fetch(`${API}/events`);
    const data = await res.json();
    setEvents(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet evenement ?')) return;
    await fetch(`${API}/admin/events/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: adminPwd })
    });
    fetchEvents();
  };

  const handleSave = async (form, isNew) => {
    const url = isNew ? `${API}/admin/events` : `${API}/admin/events/${form.id || form.event_id}`;
    const method = isNew ? 'POST' : 'PUT';
    await fetch(url, {
      method, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, password: adminPwd })
    });
    setEditing(null);
    setShowAdd(false);
    fetchEvents();
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin text-yellow-400 mx-auto" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-white">{events.length} Evenements</h3>
        <button onClick={() => setShowAdd(true)} data-testid="add-event-button"
          className="px-5 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-black rounded-xl text-sm flex items-center gap-2 hover:scale-105 transition-transform shadow-lg shadow-yellow-500/30 uppercase tracking-wide">
          <Plus size={16} /> Nouvel evenement
        </button>
      </div>

      {(showAdd || editing) && (
        <EventForm event={editing} onSave={(f) => handleSave(f, !editing)} onCancel={() => { setEditing(null); setShowAdd(false); }} />
      )}

      <div className="space-y-3">
        {events.map((e) => (
          <div key={e.id || e.event_id} data-testid={`admin-event-${e.id || e.event_id}`}
            className="flex items-center gap-4 p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl">
            {e.image ? (
              <img src={e.image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <span className="text-2xl w-14 h-14 flex items-center justify-center">{e.icon || '🎵'}</span>
            )}
            <div className="flex-1 min-w-0">
              <span className="text-white font-bold text-sm">{e.name}</span>
              <p className="text-zinc-500 text-xs">{e.type || e.event_type}</p>
              {e.dj && <p className="text-orange-400 text-xs truncate">DJs: {e.dj}</p>}
              {e.dates && e.dates[0] && <p className="text-yellow-400 text-xs">{e.dates[0].date} — {e.dates[0].venue}, {e.dates[0].city}</p>}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => setEditing(e)} data-testid={`edit-event-${e.id || e.event_id}`}
                className="p-2 bg-zinc-700 rounded-lg hover:bg-zinc-600 text-zinc-300">
                <Edit3 size={14} />
              </button>
              <button onClick={() => handleDelete(e.id || e.event_id)} data-testid={`delete-event-${e.id || e.event_id}`}
                className="p-2 bg-red-500/10 rounded-lg hover:bg-red-500/20 text-red-400">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventForm({ event, onSave, onCancel }) {
  const [form, setForm] = useState({
    id: event?.id || event?.event_id || '',
    event_id: event?.event_id || event?.id || '',
    name: event?.name || '',
    type: event?.type || event?.event_type || '',
    description: event?.description || '',
    dj: event?.dj || '',
    color: event?.color || 'from-orange-500 to-pink-500',
    image: event?.image || '',
    dates: event?.dates || [{ date: '', venue: '', city: '', time: '', status: 'confirmed' }]
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => { try { nameRef.current?.focus(); } catch (_e) { /* noop */ } }, 150);
    return () => clearTimeout(t);
  }, []);

  const nameMissing = !form.name?.trim();
  const showNameError = submitAttempted && nameMissing;
  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (nameMissing) { try { nameRef.current?.focus(); } catch (_e) { /* noop */ } return; }
    onSave(form);
  };

  const addDate = () => setForm(p => ({ ...p, dates: [...p.dates, { date: '', venue: '', city: '', time: '', status: 'confirmed' }] }));
  const removeDate = (i) => setForm(p => ({ ...p, dates: p.dates.filter((_, idx) => idx !== i) }));
  const updateDate = (i, field, val) => setForm(p => ({ ...p, dates: p.dates.map((d, idx) => idx === i ? { ...d, [field]: val } : d) }));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onCancel}
      data-testid="event-form-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="my-4 w-full max-w-3xl max-h-[92vh] bg-zinc-950 border border-yellow-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 bg-gradient-to-r from-yellow-500/15 via-orange-500/10 to-transparent border-b border-zinc-800 flex items-center justify-between shrink-0">
          <div>
            <p className="text-[10px] text-yellow-400 font-bold uppercase tracking-widest">Admin · Events</p>
            <h4 className="text-white font-black text-base">{event ? 'Modifier l\'evenement' : 'Nouvel evenement'}</h4>
          </div>
          <button onClick={onCancel} className="p-2 text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          {/* Section 1 — INFOS */}
          <section>
            <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400 mb-2">1 · Infos generales</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Nom de l'evenement *</label>
                <input ref={nameRef} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg text-white text-sm focus:outline-none ${showNameError ? 'border-red-500 ring-2 ring-red-500/40 focus:border-red-400' : 'border-zinc-800 focus:border-yellow-500'}`}
                  placeholder="Ex: SOUL TRAIN NIGHT MILANO" data-testid="event-form-name" />
                {showNameError && (
                  <p className="text-red-400 text-[11px] mt-1 font-semibold" data-testid="event-form-name-error">
                    ⚠ Le nom de l&apos;evenement est obligatoire.
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Type / genre</label>
                <input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" placeholder="House / Elektro Ka / Live..." />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">DJs / artistes</label>
                <input value={form.dj} onChange={(e) => setForm({ ...form, dj: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" placeholder="DJHALLMANN, TOM/SEA..." />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Couleur d'accent</label>
                <select value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500">
                  <option value="from-orange-500 to-pink-500">Orange → Rose</option>
                  <option value="from-yellow-500 to-orange-500">Jaune → Orange</option>
                  <option value="from-purple-500 to-pink-500">Violet → Rose</option>
                  <option value="from-cyan-500 to-blue-500">Cyan → Bleu</option>
                  <option value="from-red-500 to-yellow-500">Rouge → Jaune</option>
                </select>
              </div>
            </div>
          </section>

          {/* Section 2 — DESCRIPTION */}
          <section>
            <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400 mb-2">2 · Description</h5>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500 resize-none" rows={3}
              placeholder="Description de l'evenement (atmosphere, programmation, dresscode...)" />
          </section>

          {/* Section 3 — FLYER */}
          <section>
            <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400 mb-2">3 · Flyer / image</h5>
            <div className="flex gap-3 items-start">
              {form.image && <img src={form.image} alt="" className="w-24 h-24 rounded-lg object-cover border border-zinc-700 flex-shrink-0" />}
              <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" placeholder="https://... (URL du flyer)" />
            </div>
          </section>

          {/* Section 4 — DATES */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-[10px] font-black uppercase tracking-wider text-yellow-400">4 · Dates & lieux</h5>
              <button type="button" onClick={addDate}
                className="px-3 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 rounded-lg text-[11px] font-bold hover:bg-yellow-500/30 flex items-center gap-1">
                <Plus size={11} /> Ajouter une date
              </button>
            </div>
            <div className="space-y-2">
              {form.dates.map((d, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[140px_1fr_1fr_90px_120px_auto] gap-2 items-center bg-zinc-900/60 border border-zinc-800 rounded-lg p-2">
                  <input type="date" value={d.date} onChange={(e) => updateDate(i, 'date', e.target.value)}
                    className="px-2 py-1.5 bg-black border border-zinc-800 rounded text-white text-xs focus:outline-none focus:border-yellow-500" />
                  <input value={d.venue} onChange={(e) => updateDate(i, 'venue', e.target.value)}
                    className="px-2 py-1.5 bg-black border border-zinc-800 rounded text-white text-xs focus:outline-none focus:border-yellow-500" placeholder="Lieu / club" />
                  <input value={d.city} onChange={(e) => updateDate(i, 'city', e.target.value)}
                    className="px-2 py-1.5 bg-black border border-zinc-800 rounded text-white text-xs focus:outline-none focus:border-yellow-500" placeholder="Ville" />
                  <input value={d.time} onChange={(e) => updateDate(i, 'time', e.target.value)}
                    className="px-2 py-1.5 bg-black border border-zinc-800 rounded text-white text-xs focus:outline-none focus:border-yellow-500" placeholder="22h" />
                  <select value={d.status} onChange={(e) => updateDate(i, 'status', e.target.value)}
                    className="px-2 py-1.5 bg-black border border-zinc-800 rounded text-white text-xs focus:outline-none focus:border-yellow-500">
                    <option value="confirmed">Confirme</option>
                    <option value="a_confirmer">A confirmer</option>
                    <option value="passe">Passe</option>
                  </select>
                  {form.dates.length > 1 && (
                    <button type="button" onClick={() => removeDate(i)} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded justify-self-end"><Trash2 size={12} /></button>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="px-5 py-3.5 bg-zinc-950 border-t border-zinc-800 flex items-center justify-end gap-2 shrink-0">
          {submitAttempted && nameMissing && (
            <p className="mr-auto text-red-400 text-xs font-bold flex items-center gap-1" data-testid="event-form-footer-error">
              ⚠ Renseigne le "Nom de l&apos;evenement" pour continuer
            </p>
          )}
          <button onClick={onCancel}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm">Annuler</button>
          <button onClick={handleSubmit} data-testid="event-form-save"
            title={nameMissing ? 'Renseigne le Nom de l\'evenement' : 'Sauvegarder cet evenement'}
            className="px-5 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-black rounded-lg text-sm flex items-center gap-2 transition-colors">
            <Save size={14} /> Sauvegarder
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function TracksTab({ adminPwd }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchTracks = useCallback(async () => {
    const res = await fetch(`${API}/playlist/tracks`);
    const data = await res.json();
    setTracks(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette track ?')) return;
    await fetch(`${API}/playlist/tracks/${id}?admin_password=${adminPwd}`, { method: 'DELETE' });
    fetchTracks();
  };

  const handleSave = async ({ form, audioFile, coverFile }) => {
    // 1. Metadata
    const r1 = await fetch(`${API}/admin/playlist/tracks/${form.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, plays: parseInt(form.plays, 10) || 0, password: adminPwd }),
    });
    if (!r1.ok) {
      const d = await r1.json().catch(() => ({}));
      throw new Error(d.detail || `Erreur meta (${r1.status})`);
    }
    // 2. Files (audio and/or cover photo) — only if user picked new ones
    if (audioFile || coverFile) {
      const fd = new FormData();
      fd.append('admin_password', adminPwd);
      if (audioFile) fd.append('audio_file', audioFile);
      if (coverFile) fd.append('photo_file', coverFile);
      const r2 = await fetch(`${API}/admin/playlist/tracks/${form.id}/files`, { method: 'PUT', body: fd });
      if (!r2.ok) {
        const d = await r2.json().catch(() => ({}));
        throw new Error(d.detail || `Erreur fichiers (${r2.status})`);
      }
    }
    setEditing(null);
    fetchTracks();
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin text-yellow-400 mx-auto" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-white">{tracks.length} Tracks uploadees</h3>
        <p className="text-zinc-500 text-sm">Editez les infos + audio + cover ici, ou uploadez de nouvelles tracks via la page /radio</p>
      </div>

      {editing && (
        <TrackForm track={editing} onSave={handleSave} onCancel={() => setEditing(null)} />
      )}

      {tracks.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Music size={48} className="mx-auto mb-3 opacity-50" />
          <p>Aucune track uploadee</p>
          <p className="text-sm">Allez sur la page /radio pour uploader des tracks</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tracks.map((t) => (
            <div key={t.id} data-testid={`admin-track-${t.id}`}
              className="flex items-center gap-4 p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl">
              <Music size={20} className="text-yellow-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-white font-bold text-sm truncate block">{t.title}</span>
                <p className="text-zinc-500 text-xs">{t.artist} · {t.genre || '—'} {t.price > 0 ? `• ${t.price.toFixed(2)}€` : '• Gratuit'} · {(t.plays || 0).toLocaleString('fr-FR')} plays</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => setEditing(t)} data-testid={`edit-track-${t.id}`}
                  className="p-2 bg-zinc-700 rounded-lg hover:bg-zinc-600 text-zinc-300">
                  <Edit3 size={14} />
                </button>
                <button onClick={() => handleDelete(t.id)} data-testid={`delete-track-${t.id}`}
                  className="p-2 bg-red-500/10 rounded-lg hover:bg-red-500/20 text-red-400">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TrackForm({ track, onSave, onCancel }) {
  const [form, setForm] = useState({
    id: track?.id || '',
    title: track?.title || '',
    artist: track?.artist || '',
    author: track?.author || '',
    composer: track?.composer || '',
    genre: track?.genre || '',
    price: track?.price || 0,
    plays: track?.plays || 0,
    description: track?.description || '',
    release_date: track?.release_date || '',
    free_download: !!track?.free_download,
  });
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setSaving(true); setError('');
    try {
      await onSave({ form, audioFile, coverFile });
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    }
    setSaving(false);
  };

  const lbl = 'text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block';
  const inp = 'w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="mb-6 p-4 bg-zinc-800 border border-yellow-500/30 rounded-xl space-y-4">
      <h4 className="text-white font-bold flex items-center gap-2"><Edit3 size={16} className="text-yellow-400" /> Modifier Track</h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Titre *</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className={inp} data-testid="track-form-title" /></div>
        <div><label className={lbl}>Artiste *</label>
          <input value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })}
            className={inp} /></div>
        <div><label className={lbl}>Auteur (paroles)</label>
          <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
            className={inp} /></div>
        <div><label className={lbl}>Compositeur (musique)</label>
          <input value={form.composer} onChange={(e) => setForm({ ...form, composer: e.target.value })}
            className={inp} /></div>
        <div><label className={lbl}>Genre</label>
          <input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })}
            className={inp} /></div>
        <div><label className={lbl}>Prix EUR (0 = gratuit)</label>
          <input type="number" step="0.01" min="0" value={form.price}
            onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
            className={inp} /></div>
        <div><label className={lbl}>Date de sortie (YYYY-MM-DD)</label>
          <input type="date" value={form.release_date}
            onChange={(e) => setForm({ ...form, release_date: e.target.value })}
            className={inp} /></div>
        <div className="sm:col-span-2"><label className={lbl}>Description (racontez l histoire du track)</label>
          <textarea rows={3} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Ex: Produit a Paris en 2024, inspire par un voyage en Guadeloupe..."
            data-testid="track-form-description"
            className={inp + ' resize-none'} /></div>
        <label className="sm:col-span-2 flex items-center gap-3 p-3 bg-zinc-900/60 border border-zinc-800 rounded-lg cursor-pointer hover:border-green-500/50 transition-colors"
          data-testid="track-form-free-download-row">
          <input type="checkbox" checked={!!form.free_download}
            onChange={(e) => setForm({ ...form, free_download: e.target.checked })}
            data-testid="track-form-free-download"
            className="w-4 h-4 accent-green-500" />
          <div>
            <span className="text-sm text-white font-semibold">Telechargement gratuit autorise</span>
            <span className="block text-[10px] text-zinc-500">Si coche, un bouton Download vert apparait sur la carte de la track sur /radio</span>
          </div>
        </label>
        <div className="sm:col-span-2"><label className={lbl}>Plays — clique pour ajouter +1111 a chaque fois</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" value={form.plays}
              onChange={(e) => setForm({ ...form, plays: e.target.value })}
              className={inp} />
            <button type="button"
              onClick={() => setForm(p => ({ ...p, plays: (parseInt(p.plays, 10) || 0) + 1111 }))}
              data-testid="track-form-plays-boost"
              title="Ajoute +1111 plays a chaque clic"
              className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30 rounded-lg text-xs font-bold whitespace-nowrap">+1111 plays</button>
          </div>
        </div>
      </div>

      <section>
        <label className={lbl}>Cover / photo (jpg, png, webp)</label>
        <div className="flex items-center gap-3">
          {(track?.photo || coverFile) && (
            <img src={coverFile ? URL.createObjectURL(coverFile) : `${API}/uploads/${track.photo}`} alt=""
              className="w-16 h-16 rounded-lg object-cover border border-zinc-700" />
          )}
          <label className="flex-1 cursor-pointer">
            <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
              coverFile ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-yellow-500'
            }`}>
              <Image size={14} /> {coverFile ? coverFile.name : 'Remplacer la cover'}
            </div>
            <input type="file" accept="image/*" className="hidden"
              data-testid="track-form-cover"
              onChange={(e) => setCoverFile(e.target.files?.[0] || null)} />
          </label>
          {coverFile && <button onClick={() => setCoverFile(null)} className="text-zinc-400 hover:text-red-400 p-1"><X size={14} /></button>}
        </div>
      </section>

      <section>
        <label className={lbl}>Audio MP3/WAV (max 25 MB)</label>
        <div className="flex items-center gap-3">
          <label className="flex-1 cursor-pointer">
            <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
              audioFile ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-yellow-500'
            }`}>
              <Music size={14} />
              <span className="truncate">{audioFile ? `${audioFile.name} (${(audioFile.size / 1024 / 1024).toFixed(1)} MB)` : 'Remplacer le fichier audio (MP3/WAV/OGG/FLAC/M4A/AAC)'}</span>
            </div>
            <input type="file" accept=".mp3,.wav,.ogg,.flac,.m4a,.aac" className="hidden"
              data-testid="track-form-audio"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setError('');
                if (f && f.size > 25 * 1024 * 1024) {
                  setError(`Fichier trop lourd (${(f.size / 1024 / 1024).toFixed(1)} MB). Max : 25 MB.`);
                  setAudioFile(null);
                  e.target.value = '';
                  return;
                }
                setAudioFile(f);
              }} />
          </label>
          {audioFile && <button onClick={() => setAudioFile(null)} className="text-zinc-400 hover:text-red-400 p-1"><X size={14} /></button>}
        </div>
      </section>

      {error && <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300 flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {error}</div>}

      <div className="flex gap-2 pt-2">
        <button onClick={handleSubmit} disabled={saving || !form.title.trim() || !form.artist.trim()}
          data-testid="track-form-save"
          className="px-5 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-bold rounded-lg text-sm flex items-center gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Sauvegarder
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-zinc-700 text-white rounded-lg text-sm flex items-center gap-1">
          <X size={14} /> Annuler
        </button>
      </div>
    </motion.div>
  );
}

function CommunityTab({ adminPwd }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const audioRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const url = filter === 'pending'
      ? `${API}/admin/community/pending?password=${encodeURIComponent(adminPwd)}`
      : `${API}/admin/community/all?password=${encodeURIComponent(adminPwd)}`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setItems(filter === 'pending' ? list : list.filter(i => i.status !== 'pending' || filter === 'all').filter(i => filter === 'all' || i.status === filter));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [adminPwd, filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const togglePlay = (item) => {
    if (playingId === item.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      setPlayingId(item.id);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 50);
    }
  };

  const review = async (id, action, notes = '') => {
    if (action === 'reject' && !window.confirm('Rejeter ce track ?')) return;
    await fetch(`${API}/admin/community/${id}/${action}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: adminPwd, notes }),
    });
    if (playingId === id) { audioRef.current?.pause(); setPlayingId(null); }
    fetchItems();
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer definitivement cet upload ?')) return;
    await fetch(`${API}/admin/community/${id}?password=${encodeURIComponent(adminPwd)}`, { method: 'DELETE' });
    if (playingId === id) { audioRef.current?.pause(); setPlayingId(null); }
    fetchItems();
  };

  const filters = [
    { id: 'pending', label: 'En attente', icon: Inbox },
    { id: 'approved', label: 'Approuves', icon: ShieldCheck },
    { id: 'rejected', label: 'Rejetes', icon: ShieldAlert },
    { id: 'all', label: 'Tout', icon: Music },
  ];

  // Hidden audio element for previewing
  const playingItem = items.find(i => i.id === playingId);
  const previewSrc = playingItem
    ? `${API}/admin/community/${playingItem.id}/audio?password=${encodeURIComponent(adminPwd)}`
    : null;

  return (
    <div data-testid="admin-community-tab">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {filters.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            data-testid={`community-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all ${
              filter === f.id ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}>
            <f.icon size={12} /> {f.label}
          </button>
        ))}
      </div>

      {previewSrc && (
        <audio ref={audioRef} src={previewSrc}
          onEnded={() => setPlayingId(null)} className="hidden" />
      )}

      {loading ? (
        <div className="text-center py-12"><Loader2 className="animate-spin text-yellow-400 mx-auto" size={32} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-zinc-500" data-testid="community-empty-state">
          <Inbox size={48} className="mx-auto mb-3 opacity-50" />
          <p className="mb-1">Aucun upload {filter === 'pending' ? 'en attente' : filter === 'approved' ? 'approuve' : filter === 'rejected' ? 'rejete' : ''}.</p>
          <p className="text-xs max-w-md mx-auto mt-3 opacity-80">
            Cet onglet liste les soumissions <b>publiques</b> envoyees via la page Community.
            Pour ajouter une track <b>directement</b> a la JAH RADIO sans passer par la moderation,
            utilise l&apos;onglet <button onClick={() => window.dispatchEvent(new CustomEvent('switch-admin-tab', {detail: 'tracks'}))}
              className="text-yellow-400 underline hover:text-yellow-300 font-bold">Tracks</button>.
          </p>
          <a href="/community" target="_blank" rel="noopener" className="inline-flex items-center gap-1 mt-4 text-xs px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full text-yellow-300 hover:bg-yellow-500/20">
            <ExternalLink size={12} /> Ouvrir la page Community publique
          </a>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`admin-community-item-${it.id}`}
              className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-xl">
              <div className="flex items-start gap-3">
                {it.cover_filename ? (
                  <img src={`${API}/uploads/${it.cover_filename}`} alt={it.title}
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    <Music size={20} className="text-zinc-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <h4 className="text-white font-bold text-sm truncate">{it.title}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      it.status === 'approved' ? 'bg-yellow-500/20 text-yellow-300' :
                      it.status === 'rejected' ? 'bg-red-500/20 text-red-300' :
                      'bg-amber-500/20 text-amber-300'
                    }`}>{it.status}</span>
                  </div>
                  <p className="text-zinc-400 text-xs truncate">
                    par <span className="text-yellow-300 font-medium">{it.artist_name}</span>
                    {it.genre && ` • ${it.genre}`}
                    {it.duration > 0 && ` • ${Math.floor(it.duration / 60)}:${String(Math.floor(it.duration % 60)).padStart(2, '0')}`}
                  </p>
                  {it.email && (
                    <p className="text-zinc-500 text-[11px] flex items-center gap-1 mt-0.5 truncate">
                      <Mail size={10} /> {it.email}
                    </p>
                  )}
                  {it.description && <p className="text-zinc-300 text-xs mt-2 line-clamp-2">{it.description}</p>}
                  {it.credits && <p className="text-zinc-500 text-[11px] mt-1 truncate">Credits: {it.credits}</p>}
                  {it.admin_notes && <p className="text-amber-400 text-[11px] mt-1 italic">Note: {it.admin_notes}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-zinc-700/50">
                <button onClick={() => togglePlay(it)}
                  data-testid={`community-preview-${it.id}`}
                  className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-xs text-white flex items-center gap-1.5">
                  {playingId === it.id ? <Pause size={12} /> : <Play size={12} />}
                  {playingId === it.id ? 'Pause' : 'Ecouter'}
                </button>
                <button onClick={() => setEditing(it)}
                  data-testid={`community-edit-${it.id}`}
                  className="px-3 py-1.5 bg-blue-500 hover:bg-blue-400 rounded-lg text-xs text-white font-bold flex items-center gap-1.5">
                  <Edit3 size={12} /> Editer
                </button>
                {it.status !== 'approved' && (
                  <button onClick={() => review(it.id, 'approve')}
                    data-testid={`community-approve-${it.id}`}
                    className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 rounded-lg text-xs text-black font-bold flex items-center gap-1.5">
                    <Check size={12} /> Approuver
                  </button>
                )}
                {it.status !== 'rejected' && (
                  <button onClick={() => {
                    const notes = window.prompt('Raison du rejet (optionnel) :', '') || '';
                    review(it.id, 'reject', notes);
                  }}
                    data-testid={`community-reject-${it.id}`}
                    className="px-3 py-1.5 bg-red-500/15 hover:bg-red-500/30 border border-red-500/40 rounded-lg text-xs text-red-300 flex items-center gap-1.5">
                    <X size={12} /> Rejeter
                  </button>
                )}
                <button onClick={() => remove(it.id)}
                  data-testid={`community-delete-${it.id}`}
                  className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 rounded-lg text-xs text-zinc-400 flex items-center gap-1.5 ml-auto">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <CommunityEditModal item={editing} adminPwd={adminPwd}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchItems(); }} />
      )}
    </div>
  );
}

function CommunityEditModal({ item, adminPwd, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: item.title || '',
    artist_name: item.artist_name || '',
    genre: item.genre || '',
    price: item.price || 0,
    description: item.description || '',
    credits: item.credits || '',
    plays: item.plays || 0,
    admin_notes: item.admin_notes || '',
  });
  const [coverFile, setCoverFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const r1 = await fetch(`${API}/admin/community/${item.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          price: parseFloat(form.price) || 0,
          plays: parseInt(form.plays) || 0,
          password: adminPwd,
        }),
      });
      if (!r1.ok) {
        const d = await r1.json().catch(() => ({}));
        setError(d.detail || `Erreur meta (${r1.status})`); setSaving(false); return;
      }
      if (audioFile || coverFile) {
        const fd = new FormData();
        fd.append('admin_password', adminPwd);
        if (audioFile) fd.append('audio_file', audioFile);
        if (coverFile) fd.append('cover_file', coverFile);
        const r2 = await fetch(`${API}/admin/community/${item.id}/files`, { method: 'PUT', body: fd });
        if (!r2.ok) {
          const d = await r2.json().catch(() => ({}));
          setError(d.detail || `Erreur fichiers (${r2.status})`); setSaving(false); return;
        }
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    }
    setSaving(false);
  };

  const lbl = "text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block";
  const inp = "w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[130] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3"
      data-testid="community-edit-modal">
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="my-4 w-full max-w-2xl max-h-[92vh] bg-zinc-950 border border-blue-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 bg-gradient-to-r from-blue-500/15 to-transparent border-b border-zinc-800 flex items-center justify-between shrink-0">
          <div>
            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest">Admin · Community upload</p>
            <h4 className="text-white font-black text-base">Editer cet upload</h4>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          <section>
            <h5 className="text-[10px] font-black uppercase tracking-wider text-blue-400 mb-2">1 · Informations</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={lbl}>Titre *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className={inp} data-testid="community-edit-title" /></div>
              <div><label className={lbl}>Artiste</label>
                <input value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                  className={inp} /></div>
              <div><label className={lbl}>Genre</label>
                <input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })}
                  className={inp} /></div>
              <div><label className={lbl}>Prix EUR (0 = gratuit)</label>
                <input type="number" step="0.01" min="0" value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className={inp} /></div>
              <div className="sm:col-span-2"><label className={lbl}>Plays — clique pour ajouter +1111 a chaque fois</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" value={form.plays}
                    onChange={(e) => setForm({ ...form, plays: e.target.value })}
                    className={inp} />
                  <button type="button"
                    onClick={() => setForm(p => ({ ...p, plays: (parseInt(p.plays, 10) || 0) + 1111 }))}
                    data-testid="admin-edit-plays-boost"
                    title="Ajoute +1111 plays a chaque clic"
                    className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30 rounded-lg text-xs font-bold whitespace-nowrap">+1111 plays</button>
                </div>
              </div>
              <div className="sm:col-span-2"><label className={lbl}>Description</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={inp + ' resize-none'} /></div>
              <div className="sm:col-span-2"><label className={lbl}>Credits (auteur / compositeur)</label>
                <input value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })}
                  className={inp} /></div>
              <div className="sm:col-span-2"><label className={lbl}>Note admin (interne)</label>
                <input value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
                  className={inp} placeholder="Visible uniquement dans le dashboard admin" /></div>
            </div>
          </section>

          <section>
            <h5 className="text-[10px] font-black uppercase tracking-wider text-blue-400 mb-2">2 · Cover photo</h5>
            <div className="flex items-center gap-3">
              {(item.cover_filename || coverFile) && (
                <img src={coverFile ? URL.createObjectURL(coverFile) : `${API}/uploads/${item.cover_filename}`} alt=""
                  className="w-16 h-16 rounded-lg object-cover border border-zinc-700" />
              )}
              <label className="flex-1 cursor-pointer">
                <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                  coverFile ? 'bg-blue-500/20 border-blue-500/50 text-blue-300' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-blue-500'
                }`}>
                  <Image size={14} /> {coverFile ? coverFile.name : 'Remplacer la cover (jpg/png/webp)'}
                </div>
                <input type="file" accept="image/*" className="hidden"
                  data-testid="community-edit-cover"
                  onChange={(e) => setCoverFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          </section>

          <section>
            <h5 className="text-[10px] font-black uppercase tracking-wider text-blue-400 mb-2">3 · Audio MP3/WAV (max 25 MB)</h5>
            <label className="cursor-pointer block">
              <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                audioFile ? 'bg-blue-500/20 border-blue-500/50 text-blue-300' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-blue-500'
              }`}>
                <Music size={14} />
                <span className="truncate">{audioFile ? `${audioFile.name} (${(audioFile.size / 1024 / 1024).toFixed(1)} MB)` : 'Remplacer le fichier audio'}</span>
              </div>
              <input type="file" accept=".mp3,.wav,.ogg,.flac,.m4a,.aac" className="hidden"
                data-testid="community-edit-audio"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setError('');
                  if (f && f.size > 25 * 1024 * 1024) {
                    setError(`Fichier trop lourd (${(f.size / 1024 / 1024).toFixed(1)} MB). Max : 25 MB.`);
                    setAudioFile(null);
                    e.target.value = '';
                    return;
                  }
                  setAudioFile(f);
                }} />
            </label>
          </section>

          {error && <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300">{error}</div>}
        </div>

        <div className="px-5 py-3.5 bg-zinc-950 border-t border-zinc-800 flex items-center justify-end gap-2 shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm">Annuler</button>
          <button onClick={save} disabled={saving || !form.title.trim()}
            data-testid="community-edit-save"
            className="px-5 py-2 bg-blue-500 hover:bg-blue-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-white font-black rounded-lg text-sm flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Sauvegarder
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function MigrateButton({ adminPwd }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [forceMode, setForceMode] = useState(false);

  const run = async () => {
    if (forceMode && !window.confirm("FORCER ecrase les videos artistes par les defaults. Continuer ?")) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/admin/migrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPwd, force: forceMode }),
      });
      const d = await r.json();
      if (!r.ok) {
        alert('Erreur: ' + (d.detail || r.status));
        return;
      }
      setResult(d);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <label className="flex items-center gap-1.5 text-[10px] text-zinc-400 cursor-pointer">
        <input type="checkbox" checked={forceMode} onChange={(e) => setForceMode(e.target.checked)}
          className="w-3 h-3 accent-emerald-500" />
        Forcer ecraser
      </label>
      <button onClick={run} disabled={running}
        data-testid="admin-migrate-button"
        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-lg text-xs disabled:opacity-50 inline-flex items-center gap-1.5">
        {running ? <><Loader2 size={12} className="animate-spin" /> Migration...</> : <>{forceMode ? 'Forcer Sync' : 'Synchroniser'}</>}
      </button>
      {result && (
        <div className="text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-2 py-1.5 max-w-xs"
          data-testid="admin-migrate-result">
          ✓ {result.artists_videos_added} artistes mis a jour<br/>
          ✓ {result.settings_added} settings ajoutes<br/>
          ✓ Total: {result.total_videos} videos / {result.total_artists} artistes
        </div>
      )}
    </div>
  );
}

function SiteTab({ adminPwd }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [savedKey, setSavedKey] = useState('');

  const TAGLINES = [
    { key: 'tagline_home', label: 'Home — Accueil' },
    { key: 'tagline_shop', label: 'Boutique' },
    { key: 'tagline_elektroka', label: 'Elektro Ka' },
    { key: 'tagline_studio', label: 'KAZAJAH AI Studio' },
    { key: 'tagline_playlist', label: 'KAZAJAH Radio' },
    { key: 'tagline_events', label: 'Evenements' },
    { key: 'tagline_community', label: 'Community' },
    { key: 'tagline_gallery', label: 'KAZAJAH TV (Galerie)' },
  ];

  const STREAMING_FIELDS = [
    { key: 'spotify', label: 'Spotify URL' },
    { key: 'apple_music', label: 'Apple Music URL' },
    { key: 'youtube', label: 'YouTube URL' },
    { key: 'soundcloud', label: 'SoundCloud URL' },
    { key: 'deezer', label: 'Deezer URL' },
    { key: 'facebook', label: 'Facebook URL' },
    { key: 'instagram', label: 'Instagram URL' },
  ];

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/settings`);
      const d = await r.json();
      setSettings(d);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async (key, value) => {
    setSaving(key);
    setSavedKey('');
    try {
      const r = await fetch(`${API}/admin/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPwd, value }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert('Erreur: ' + (d.detail || r.status));
        return;
      }
      setSavedKey(key);
      setTimeout(() => setSavedKey(''), 1500);
      fetchSettings();
    } finally {
      setSaving('');
    }
  };

  const reset = async (key) => {
    if (!window.confirm(`Reinitialiser "${key}" a sa valeur par defaut ?`)) return;
    setSaving(key);
    await fetch(`${API}/admin/settings/reset/${key}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: adminPwd }),
    });
    setSaving('');
    fetchSettings();
  };

  if (loading || !settings) {
    return <div className="text-center py-12"><Loader2 className="animate-spin text-yellow-400 mx-auto" size={28} /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-site-tab">
      {/* === DATA SYNC / MIGRATION === */}
      <section className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="text-sm font-bold text-emerald-300 mb-1 flex items-center gap-2">
              <ShieldCheck size={14} /> Synchroniser les donnees du site
            </h3>
            <p className="text-xs text-zinc-400">
              Apres un deploiement en production, clique ici pour seeder les videos artistes manquantes,
              les settings par defaut, etc. <strong className="text-emerald-300">Ne supprime jamais</strong>{' '}
              les donnees admin existantes (par defaut). Active "Forcer" pour ecraser les videos par les valeurs par defaut.
            </p>
          </div>
          <MigrateButton adminPwd={adminPwd} />
        </div>
      </section>

      {/* === TAGLINES === */}
      <section>
        <h3 className="text-base font-bold text-yellow-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Edit3 size={14} /> Phrases d'impact (par page)
        </h3>
        <p className="text-xs text-zinc-500 mb-4">Le texte affiche en banniere en haut de chaque page du site.</p>
        <div className="space-y-3">
          {TAGLINES.map(t => (
            <SettingTextRow key={t.key} settingKey={t.key} label={t.label}
              value={settings[t.key] || ''}
              saving={saving === t.key}
              saved={savedKey === t.key}
              onSave={(v) => save(t.key, v)}
              onReset={() => reset(t.key)} multiline />
          ))}
        </div>
      </section>

      {/* === STUDIO LOCATIONS === */}
      <section>
        <h3 className="text-base font-bold text-yellow-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <MapPin size={14} /> Locations Studio
        </h3>
        <p className="text-xs text-zinc-500 mb-4">Les villes affichees dans la section Studio sur la home page.</p>
        <LocationsEditor
          value={settings.studio_locations || []}
          saving={saving === 'studio_locations'}
          saved={savedKey === 'studio_locations'}
          onSave={(v) => save('studio_locations', v)}
          onReset={() => reset('studio_locations')} />
      </section>

      {/* === STREAMING LINKS === */}
      <section>
        <h3 className="text-base font-bold text-yellow-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Headphones size={14} /> Liens Streaming
        </h3>
        <p className="text-xs text-zinc-500 mb-4">Les boutons sur la page d'accueil — section Streaming & Videos.</p>
        <StreamingEditor fields={STREAMING_FIELDS}
          value={settings.streaming_links || {}}
          saving={saving === 'streaming_links'}
          saved={savedKey === 'streaming_links'}
          onSave={(v) => save('streaming_links', v)}
          onReset={() => reset('streaming_links')} />
      </section>

      {/* === NOTIFICATIONS EMAIL === */}
      <section>
        <h3 className="text-base font-bold text-yellow-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Mail size={14} /> Notifications email admin
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Adresse a laquelle les ventes PayPal et les tips artiste sont envoyes automatiquement.
          Tu peux la changer ici a tout moment, sans redeployer.
        </p>
        <SettingTextRow
          settingKey="admin_notification_email"
          label="Email destinataire"
          value={settings.admin_notification_email || ''}
          saving={saving === 'admin_notification_email'}
          saved={savedKey === 'admin_notification_email'}
          onSave={(v) => save('admin_notification_email', v)}
          onReset={() => reset('admin_notification_email')}
        />
      </section>
    </div>
  );
}

function SettingTextRow({ settingKey, label, value, saving, saved, onSave, onReset, multiline }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const dirty = draft !== value;
  return (
    <div className="bg-zinc-800/40 border border-zinc-800 rounded-xl p-3" data-testid={`setting-${settingKey}`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-bold text-zinc-300">{label}</label>
        <div className="flex items-center gap-2">
          {saved && <span className="text-emerald-400 text-[10px] font-bold flex items-center gap-1"><Check size={10} /> Sauve</span>}
          <button onClick={onReset} className="text-[10px] text-zinc-500 hover:text-yellow-400" title="Reinitialiser">Reset</button>
        </div>
      </div>
      {multiline ? (
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500 resize-none" />
      ) : (
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-500" />
      )}
      <div className="flex justify-end mt-2">
        <button onClick={() => onSave(draft)} disabled={!dirty || saving}
          data-testid={`save-${settingKey}`}
          className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg text-xs disabled:opacity-30 inline-flex items-center gap-1.5">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Sauvegarder
        </button>
      </div>
    </div>
  );
}

function LocationsEditor({ value, saving, saved, onSave, onReset }) {
  const [list, setList] = useState(value);
  useEffect(() => { setList(value); }, [value]);
  const upd = (i, k, v) => setList(list.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const add = () => setList([...list, { city: '', place: '', flag: '🌍' }]);
  const del = (i) => setList(list.filter((_, idx) => idx !== i));
  const dirty = JSON.stringify(list) !== JSON.stringify(value);

  return (
    <div className="bg-zinc-800/40 border border-zinc-800 rounded-xl p-3">
      <div className="space-y-2">
        {list.map((loc, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`location-row-${i}`}>
            <input value={loc.flag} onChange={(e) => upd(i, 'flag', e.target.value)}
              className="w-12 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-white text-base text-center"
              placeholder="🇫🇷" maxLength={4} />
            <input value={loc.city} onChange={(e) => upd(i, 'city', e.target.value)}
              className="flex-1 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-white text-xs"
              placeholder="Ville" />
            <input value={loc.place} onChange={(e) => upd(i, 'place', e.target.value)}
              className="flex-1 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-white text-xs"
              placeholder="Lieu" />
            <button onClick={() => del(i)} className="p-1 text-red-400 hover:bg-red-500/20 rounded"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <button onClick={add}
          className="px-2 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded text-[11px] font-bold hover:bg-yellow-500/30 inline-flex items-center gap-1">
          <Plus size={11} /> Ajouter une ville
        </button>
        <div className="flex items-center gap-2">
          {saved && <span className="text-emerald-400 text-[10px] font-bold flex items-center gap-1"><Check size={10} /> Sauve</span>}
          <button onClick={onReset} className="text-[10px] text-zinc-500 hover:text-yellow-400">Reset</button>
          <button onClick={() => onSave(list)} disabled={!dirty || saving}
            data-testid="save-studio-locations"
            className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg text-xs disabled:opacity-30 inline-flex items-center gap-1.5">
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
}

function StreamingEditor({ fields, value, saving, saved, onSave, onReset }) {
  const [obj, setObj] = useState(value);
  useEffect(() => { setObj(value); }, [value]);
  const upd = (k, v) => setObj({ ...obj, [k]: v });
  const dirty = JSON.stringify(obj) !== JSON.stringify(value);

  return (
    <div className="bg-zinc-800/40 border border-zinc-800 rounded-xl p-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {fields.map(f => (
          <div key={f.key} data-testid={`streaming-${f.key}`}>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5 block">{f.label}</label>
            <input value={obj[f.key] || ''} onChange={(e) => upd(f.key, e.target.value)}
              className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-white text-xs focus:outline-none focus:border-yellow-500"
              placeholder={`https://...`} />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        {saved && <span className="text-emerald-400 text-[10px] font-bold flex items-center gap-1"><Check size={10} /> Sauve</span>}
        <button onClick={onReset} className="text-[10px] text-zinc-500 hover:text-yellow-400">Reset</button>
        <button onClick={() => onSave(obj)} disabled={!dirty || saving}
          data-testid="save-streaming-links"
          className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg text-xs disabled:opacity-30 inline-flex items-center gap-1.5">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Sauvegarder
        </button>
      </div>
    </div>
  );
}

// ============ NEWSLETTER TAB ============
function NewsletterTab({ adminPwd }) {
  const [data, setData] = useState({ count: 0, subscribers: [] });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [broadcast, setBroadcast] = useState({ subject: '', html: '' });
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/newsletter/subscribers?password=${encodeURIComponent(adminPwd)}&status=${statusFilter}`);
      const d = await res.json();
      setData(d);
    } catch {
      setData({ count: 0, subscribers: [] });
    }
    setLoading(false);
  }, [adminPwd, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleExportCsv = () => {
    window.open(`${API}/admin/newsletter/export.csv?password=${encodeURIComponent(adminPwd)}`, '_blank');
  };

  const handleBroadcast = async (testOnly) => {
    if (!broadcast.subject.trim() || !broadcast.html.trim()) {
      setFeedback('Sujet et contenu requis');
      return;
    }
    setSending(true);
    setFeedback('');
    try {
      const res = await fetch(`${API}/admin/newsletter/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPwd, subject: broadcast.subject, html: broadcast.html, test_only: !!testOnly })
      });
      const d = await res.json();
      if (res.ok) {
        setFeedback(`Envoye a ${d.recipients} destinataire(s)${d.test ? ' (test)' : ''}`);
        if (!testOnly) setBroadcast({ subject: '', html: '' });
      } else {
        setFeedback(d.detail || 'Erreur envoi');
      }
    } catch (e) {
      setFeedback(e.message || 'Erreur reseau');
    }
    setSending(false);
  };

  return (
    <div data-testid="admin-newsletter-tab" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Newsletter</h2>
          <p className="text-sm text-zinc-500">{data.count} abonne{data.count !== 1 ? 's' : ''} ({statusFilter})</p>
        </div>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="newsletter-status-filter"
            className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white">
            <option value="active">Actifs</option>
            <option value="unsubscribed">Désinscrits</option>
            <option value="all">Tous</option>
          </select>
          <button onClick={handleExportCsv} data-testid="newsletter-export-csv"
            className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm font-bold flex items-center gap-2">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {/* Broadcast composer */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 space-y-3">
        <h3 className="font-bold text-amber-400 flex items-center gap-2"><Send size={16} /> Envoyer une newsletter</h3>
        <input data-testid="newsletter-broadcast-subject"
          value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })}
          placeholder="Sujet du message"
          className="w-full bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white" />
        <textarea data-testid="newsletter-broadcast-html"
          value={broadcast.html} onChange={(e) => setBroadcast({ ...broadcast, html: e.target.value })}
          placeholder="Contenu HTML — utilise des <p>, <a>, <b>… (sera affiché tel quel)"
          rows={8}
          className="w-full bg-black border border-zinc-800 rounded-lg px-3 py-2 text-xs text-white font-mono resize-none" />
        {feedback && <p className="text-xs text-zinc-400">{feedback}</p>}
        <div className="flex gap-2">
          <button onClick={() => handleBroadcast(true)} disabled={sending} data-testid="newsletter-broadcast-test"
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm font-bold disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin inline" /> : 'Test admin'}
          </button>
          <button onClick={() => {
            if (window.confirm(`Envoyer a ${data.count} abonne(s) ?`)) handleBroadcast(false);
          }} disabled={sending || data.count === 0} data-testid="newsletter-broadcast-send"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-sm font-bold disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin inline" /> : `Envoyer (${data.count})`}
          </button>
        </div>
      </div>

      {/* Subscriber list */}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="animate-spin text-amber-400" /></div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
          {data.subscribers.length === 0 ? (
            <p className="text-zinc-500 text-sm p-6 text-center">Aucun abonne</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-950 text-zinc-500 text-xs uppercase">
                <tr><th className="p-3 text-left">Email</th><th className="p-3 text-left">Source</th><th className="p-3 text-left">Inscrit le</th></tr>
              </thead>
              <tbody>
                {data.subscribers.slice(0, 200).map((s, i) => (
                  <tr key={s.id || i} className="border-t border-zinc-800/50">
                    <td className="p-3 text-white font-mono text-xs">{s.email}</td>
                    <td className="p-3 text-zinc-500">{s.source || '—'}</td>
                    <td className="p-3 text-zinc-500">{s.subscribed_at?.slice(0, 10) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ============ SERVICES PRO TAB ============
function ServicesProTab({ adminPwd }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/services-pro/bookings?password=${encodeURIComponent(adminPwd)}`);
      const d = await res.json();
      setBookings(Array.isArray(d) ? d : []);
    } catch {
      setBookings([]);
    }
    setLoading(false);
  }, [adminPwd]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    const res = await fetch(`${API}/admin/services-pro/bookings/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: adminPwd, status })
    });
    if (res.ok) load();
  };

  return (
    <div data-testid="admin-services-pro-tab" className="space-y-4">
      <h2 className="text-xl font-bold">Commandes Services Pro</h2>
      <p className="text-sm text-zinc-500">{bookings.length} commande{bookings.length !== 1 ? 's' : ''}</p>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="animate-spin text-amber-400" /></div>
      ) : bookings.length === 0 ? (
        <p className="text-zinc-500 text-sm p-6 text-center bg-zinc-900 rounded-2xl border border-zinc-800">Aucune commande pour le moment</p>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex-1 min-w-[60%]">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-white">{b.service_name}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300">{b.category}</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">
                    <b className="text-white">{b.customer_name}</b> &lt;{b.customer_email}&gt; · <b className="text-amber-400">{(b.price || 0).toFixed(2)} €</b>
                  </p>
                  {b.project_title && <p className="text-xs text-zinc-500 mt-1">Projet : {b.project_title}</p>}
                  {b.notes && <p className="text-xs text-zinc-500 mt-1 whitespace-pre-line">Notes : {b.notes}</p>}
                  <p className="text-[10px] text-zinc-600 mt-2 font-mono">{b.paypal_order_id} · {b.created_at?.slice(0, 16).replace('T', ' ')}</p>
                </div>
                <select value={b.status || 'pending'}
                  onChange={(e) => updateStatus(b.id, e.target.value)}
                  data-testid={`booking-status-${b.id}`}
                  className="bg-black border border-zinc-800 rounded-lg px-3 py-1.5 text-xs text-white">
                  <option value="pending">À traiter</option>
                  <option value="in_progress">En cours</option>
                  <option value="delivered">Livré</option>
                  <option value="cancelled">Annulé</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ BEAT SAMPLES TAB ============
function BeatSamplesTab({ adminPwd }) {
  const [samples, setSamples] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadName, setUploadName] = useState('');
  const [uploadSlot, setUploadSlot] = useState('free');
  const [uploadPack, setUploadPack] = useState('Library');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [activePackFilter, setActivePackFilter] = useState('');
  const fileRef = useRef(null);

  // Slice-from-track state
  const [tracksList, setTracksList] = useState([]);
  const [sliceTrackId, setSliceTrackId] = useState('');
  const [sliceSegments, setSliceSegments] = useState(8);
  const [slicePackName, setSlicePackName] = useState('');
  const [slicing, setSlicing] = useState(false);
  const [sliceProgress, setSliceProgress] = useState(0);
  const [sliceMsg, setSliceMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, tr] = await Promise.all([
        fetch(`${API}/beatmaker/samples`).then(r => r.json()),
        fetch(`${API}/beatmaker/packs`).then(r => r.json()),
        fetch(`${API}/playlist/tracks`).then(r => r.json()),
      ]);
      setSamples(Array.isArray(s) ? s : []);
      setPacks(Array.isArray(p) ? p : []);
      setTracksList(Array.isArray(tr) ? tr : []);
    } catch { setSamples([]); setPacks([]); setTracksList([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) { setMsg('Nom + fichier requis'); return; }
    setUploading(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('admin_password', adminPwd);
      fd.append('name', uploadName.trim());
      fd.append('slot', uploadSlot);
      fd.append('pack_name', uploadPack.trim() || 'Library');
      fd.append('file', uploadFile);
      const r = await fetch(`${API}/admin/beatmaker/samples`, { method: 'POST', body: fd });
      const d = await r.json();
      if (r.ok) {
        setMsg(`OK : "${d.name}" ajoute au pack "${d.pack_name}"`);
        setUploadName(''); setUploadFile(null); if (fileRef.current) fileRef.current.value = '';
        load();
      } else {
        setMsg(d.detail || 'Erreur upload');
      }
    } catch (e) {
      setMsg(e.message || 'Erreur reseau');
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce sample ?')) return;
    await fetch(`${API}/admin/beatmaker/samples/${id}?admin_password=${encodeURIComponent(adminPwd)}`, { method: 'DELETE' });
    load();
  };

  const handleDeletePack = async (packName) => {
    if (!window.confirm(`Supprimer le pack "${packName}" et TOUS ses samples ?`)) return;
    await fetch(`${API}/admin/beatmaker/packs/${encodeURIComponent(packName)}?admin_password=${encodeURIComponent(adminPwd)}`, { method: 'DELETE' });
    load();
  };

  /** Slice a radio track into N samples and upload each as a new sample in the pack.
   *  Runs entirely client-side using Web Audio API + the audioBufferToWavBlob helper.
   */
  const handleSliceTrack = async () => {
    if (!sliceTrackId || !slicePackName.trim()) { setSliceMsg('Track + nom pack requis'); return; }
    const track = tracksList.find(t => t.id === sliceTrackId);
    if (!track) { setSliceMsg('Track introuvable'); return; }
    setSlicing(true); setSliceProgress(0); setSliceMsg('Telechargement du track...');
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${track.filename}`;
      const buffer = await decodeAudioUrl(audioCtx, url);
      const totalDur = buffer.duration;
      const segDur = totalDur / sliceSegments;
      const packName = slicePackName.trim();

      for (let i = 0; i < sliceSegments; i++) {
        setSliceMsg(`Decoupage ${i + 1}/${sliceSegments}...`);
        const startSec = i * segDur;
        const endSec = (i + 1) * segDur;
        const slice = sliceAudioBuffer(audioCtx, buffer, startSec, endSec);
        const blob = audioBufferToWavBlob(slice);

        const fd = new FormData();
        fd.append('admin_password', adminPwd);
        fd.append('name', `${track.title || 'Track'} - Slice ${i + 1}`);
        fd.append('slot', 'free');
        fd.append('pack_name', packName);
        fd.append('file', blob, `slice_${i + 1}.wav`);
        const r = await fetch(`${API}/admin/beatmaker/samples`, { method: 'POST', body: fd });
        if (!r.ok) throw new Error(`Upload slice ${i + 1} failed`);
        setSliceProgress(Math.round(((i + 1) / sliceSegments) * 100));
      }

      setSliceMsg(`Pack "${packName}" cree (${sliceSegments} slices) !`);
      setSliceTrackId(''); setSlicePackName('');
      load();
    } catch (e) {
      setSliceMsg(`Erreur : ${e.message || 'inconnue'}`);
    }
    setSlicing(false);
  };

  // Group samples by pack for the listing
  const samplesByPack = samples.reduce((acc, s) => {
    const k = s.pack_name || 'Library';
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});

  const visiblePacks = activePackFilter
    ? { [activePackFilter]: samplesByPack[activePackFilter] || [] }
    : samplesByPack;

  return (
    <div data-testid="admin-beat-samples-tab" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Beat Samples & Packs</h2>
          <p className="text-sm text-zinc-500">{samples.length} sample(s) dans {packs.length} pack(s). Visibles dans le Beat Maker en mode CUSTOM.</p>
        </div>
      </div>

      {/* Slice from radio track */}
      <div className="bg-zinc-900 border border-fuchsia-500/30 rounded-2xl p-4 space-y-3">
        <h3 className="font-bold text-fuchsia-400 flex items-center gap-2">
          <Sparkles size={16} /> Créer un pack depuis un track radio
        </h3>
        <p className="text-[11px] text-zinc-500">Découpe automatiquement le track choisi en N samples et les ajoute à un nouveau pack. Découpage côté navigateur (rapide, sans serveur).</p>
        <div className="grid sm:grid-cols-4 gap-2">
          <select data-testid="slice-track-select" value={sliceTrackId} onChange={(e) => {
            setSliceTrackId(e.target.value);
            const t = tracksList.find(x => x.id === e.target.value);
            if (t && !slicePackName) setSlicePackName(`${(t.artist || 'KAZAJAH')} - ${(t.title || '')}`.slice(0, 60));
          }}
            className="sm:col-span-2 bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white">
            <option value="">— Choisir un track —</option>
            {tracksList.map(t => (
              <option key={t.id} value={t.id}>{(t.artist || '?')} - {(t.title || '?').slice(0, 40)}</option>
            ))}
          </select>
          <input data-testid="slice-pack-name" placeholder="Nom du pack" value={slicePackName}
            onChange={(e) => setSlicePackName(e.target.value)}
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white" />
          <select data-testid="slice-segments" value={sliceSegments} onChange={(e) => setSliceSegments(parseInt(e.target.value))}
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white">
            <option value="4">4 slices</option>
            <option value="8">8 slices</option>
            <option value="16">16 slices</option>
            <option value="32">32 slices</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleSliceTrack} disabled={slicing || !sliceTrackId || !slicePackName.trim()}
            data-testid="slice-generate"
            className="px-4 py-2 bg-fuchsia-500 hover:bg-fuchsia-400 text-black rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-2">
            {slicing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {slicing ? `${sliceProgress}%` : 'Générer le pack'}
          </button>
          {sliceMsg && <p className="text-xs text-zinc-400 flex-1">{sliceMsg}</p>}
        </div>
      </div>

      {/* Upload form */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 space-y-3">
        <h3 className="font-bold text-yellow-400 flex items-center gap-2"><Upload size={16} /> Ajouter un sample manuel</h3>
        <div className="grid sm:grid-cols-4 gap-2">
          <input data-testid="bs-name" type="text" placeholder="Nom du sample" value={uploadName}
            onChange={(e) => setUploadName(e.target.value)}
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white" />
          <input data-testid="bs-pack" type="text" placeholder="Pack (ex: KAZAJAH Drums)" value={uploadPack}
            onChange={(e) => setUploadPack(e.target.value)}
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white"
            list="bs-pack-suggestions" />
          <datalist id="bs-pack-suggestions">
            {packs.map(p => <option key={p.name} value={p.name} />)}
          </datalist>
          <select data-testid="bs-slot" value={uploadSlot} onChange={(e) => setUploadSlot(e.target.value)}
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white">
            <option value="free">Slot libre</option>
            <option value="boula">Boula</option>
            <option value="markeur">Markeur</option>
            <option value="tibwa">Ti-bwa</option>
            <option value="chacha">Chacha</option>
          </select>
          <input ref={fileRef} data-testid="bs-file" type="file" accept="audio/*"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white file:text-yellow-400 file:bg-transparent file:border-0" />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleUpload} disabled={uploading || !uploadFile} data-testid="bs-upload"
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg text-sm font-bold disabled:opacity-50">
            {uploading ? <Loader2 size={14} className="animate-spin inline" /> : 'Uploader'}
          </button>
          {msg && <p className="text-xs text-zinc-400">{msg}</p>}
        </div>
      </div>

      {/* Pack filter chips */}
      {packs.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] text-zinc-500 uppercase font-bold">Filtre pack</span>
          <button onClick={() => setActivePackFilter('')}
            className={`px-2.5 py-1 text-[10px] rounded-full font-bold transition-colors ${!activePackFilter ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>
            Tous
          </button>
          {packs.map(p => (
            <button key={p.name} onClick={() => setActivePackFilter(p.name)}
              data-testid={`bs-filter-pack-${p.name}`}
              className={`px-2.5 py-1 text-[10px] rounded-full font-bold transition-colors flex items-center gap-1 ${activePackFilter === p.name ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
              {p.name}
              <span className="text-[9px] opacity-70">({p.count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Samples list — grouped by pack */}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="animate-spin text-yellow-400" /></div>
      ) : samples.length === 0 ? (
        <p className="text-zinc-500 text-sm p-6 text-center bg-zinc-900 rounded-2xl border border-zinc-800">Aucun sample pour le moment</p>
      ) : (
        <div className="space-y-6">
          {Object.entries(visiblePacks).map(([packName, items]) => (
            <div key={packName}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-zinc-300 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-zinc-800 rounded">{packName}</span>
                  <span className="text-[10px] text-zinc-500">{items.length} sample{items.length > 1 ? 's' : ''}</span>
                </h3>
                <button onClick={() => handleDeletePack(packName)}
                  data-testid={`bs-delete-pack-${packName}`}
                  className="text-[10px] text-zinc-500 hover:text-red-400 flex items-center gap-1">
                  <Trash2 size={11} /> Supprimer pack
                </button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((s) => (
                  <div key={s.id} data-testid={`bs-item-${s.id}`}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-white text-sm truncate">{s.name}</h4>
                        <p className="text-[10px] text-zinc-500">Slot : <span className="text-zinc-300">{s.slot}</span> · {(s.size_bytes / 1024).toFixed(0)} Ko</p>
                      </div>
                      <button onClick={() => handleDelete(s.id)} className="text-zinc-500 hover:text-red-400">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} className="w-full h-7" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ AI CREDITS TAB ============
function AICreditsTab({ adminPwd }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantAmount, setGrantAmount] = useState(50);
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantMsg, setGrantMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/ai-credits/users?password=${encodeURIComponent(adminPwd)}`);
      const d = await res.json();
      setUsers(d.users || []);
    } catch {
      setUsers([]);
    }
    setLoading(false);
  }, [adminPwd]);

  useEffect(() => { load(); }, [load]);

  const handleGrant = async () => {
    if (!grantEmail.includes('@') || grantAmount === 0) {
      setGrantMsg('Email + nombre de credits requis');
      return;
    }
    setGranting(true);
    setGrantMsg('');
    try {
      const res = await fetch(`${API}/admin/ai-credits/grant`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPwd, email: grantEmail, credits: parseInt(grantAmount), note: grantNote })
      });
      const d = await res.json();
      if (res.ok) {
        setGrantMsg(`OK : ${grantEmail} a maintenant ${d.balance.balance} credits`);
        setGrantEmail(''); setGrantNote(''); setGrantAmount(50);
        load();
      } else {
        setGrantMsg(d.detail || 'Erreur');
      }
    } catch (e) {
      setGrantMsg(e.message || 'Erreur reseau');
    }
    setGranting(false);
  };

  const totalCredits = users.reduce((sum, u) => sum + (u.balance || 0), 0);
  const totalEarned = users.reduce((sum, u) => sum + (u.total_earned || 0), 0);

  return (
    <div data-testid="admin-ai-credits-tab" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold">AI Credits</h2>
          <p className="text-sm text-zinc-500">{users.length} utilisateur(s) · {totalCredits} crédits en circulation · {totalEarned} créés au total</p>
        </div>
      </div>

      {/* Grant credits form */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 space-y-3">
        <h3 className="font-bold text-amber-400 flex items-center gap-2"><Sparkles size={16} /> Offrir des crédits (manuellement)</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <input data-testid="ai-grant-email" type="email" placeholder="email@artiste.com" value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white" />
          <input data-testid="ai-grant-amount" type="number" value={grantAmount}
            onChange={(e) => setGrantAmount(e.target.value)} placeholder="50"
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white" />
          <input data-testid="ai-grant-note" placeholder="Note (ex: artiste label)" value={grantNote}
            onChange={(e) => setGrantNote(e.target.value)}
            className="bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white" />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleGrant} disabled={granting} data-testid="ai-grant-submit"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-sm font-bold disabled:opacity-50">
            {granting ? <Loader2 size={14} className="animate-spin inline" /> : 'Créditer'}
          </button>
          {grantMsg && <p className="text-xs text-zinc-400">{grantMsg}</p>}
        </div>
        <p className="text-[10px] text-zinc-600">Utilise une valeur négative pour retirer des crédits. Note conservée dans l'historique.</p>
      </div>

      {/* Users table */}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="animate-spin text-amber-400" /></div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
          {users.length === 0 ? (
            <p className="text-zinc-500 text-sm p-6 text-center">Aucun utilisateur pour le moment</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-950 text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-right">Solde</th>
                  <th className="p-3 text-right">Gagné</th>
                  <th className="p-3 text-right">Utilisé</th>
                  <th className="p-3 text-left">Abo</th>
                </tr>
              </thead>
              <tbody>
                {users.slice(0, 300).map((u, i) => (
                  <tr key={u.email || i} className="border-t border-zinc-800/50">
                    <td className="p-3 text-white font-mono text-xs">{u.email}</td>
                    <td className="p-3 text-right text-amber-400 font-bold">{u.balance || 0}</td>
                    <td className="p-3 text-right text-zinc-400">{u.total_earned || 0}</td>
                    <td className="p-3 text-right text-zinc-400">{u.total_consumed || 0}</td>
                    <td className="p-3 text-xs text-zinc-500">
                      {u.subscription_expires_at ? (
                        <span className="text-purple-300">jusqu'au {u.subscription_expires_at.slice(0, 10)}</span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ============ BACKUP TAB ============
function BackupTab({ adminPwd }) {
  const [downloading, setDownloading] = useState('');

  const downloadZip = (scope) => {
    setDownloading(scope);
    const url = `${API}/admin/backup/all.zip?admin_password=${encodeURIComponent(adminPwd)}&scope=${scope}`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => setDownloading(''), 2500);
  };

  const items = [
    { id: 'all', label: 'Tout (Radio + Community + AI)', desc: 'Backup complet de tous les audios', cls: 'bg-amber-500 hover:bg-amber-400' },
    { id: 'playlist', label: 'JAH RADIO', desc: 'Toutes les tracks de la radio', cls: 'bg-orange-500 hover:bg-orange-400' },
    { id: 'community', label: 'Community', desc: 'Toutes les soumissions (pending + approved + rejected)', cls: 'bg-yellow-500 hover:bg-yellow-400' },
    { id: 'ai', label: 'AI Music', desc: 'Toutes les générations IA', cls: 'bg-emerald-500 hover:bg-emerald-400' },
  ];

  return (
    <div data-testid="admin-backup-tab" className="space-y-4">
      <div>
        <h2 className="text-xl font-bold">Backup audio</h2>
        <p className="text-sm text-zinc-500">Télécharge un ZIP de tous les fichiers audio. Pratique pour archive offline.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((it) => (
          <div key={it.id} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white">{it.label}</h3>
              <p className="text-xs text-zinc-500 mt-1">{it.desc}</p>
            </div>
            <button
              data-testid={`backup-${it.id}`}
              onClick={() => downloadZip(it.id)}
              disabled={downloading === it.id}
              className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors text-black disabled:opacity-60 ${it.cls}`}>
              {downloading === it.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              ZIP
            </button>
          </div>
        ))}
      </div>
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-xs text-zinc-500">
        <b className="text-zinc-300">Note :</b> les ZIP peuvent être volumineux (centaines de Mo).
        Le téléchargement démarre côté serveur — patiente quelques secondes pendant la compression.
      </div>
    </div>
  );
}

export default function AdminDashboard({ onBack }) {
  const ADMIN_LS_KEY = 'kazajah_admin_pwd';
  // Read existing admin password from localStorage on mount so that a user who
  // already unlocked admin somewhere else on the site stays logged in here too.
  const initialPwd = (() => {
    try { return localStorage.getItem(ADMIN_LS_KEY) || ''; } catch { return ''; }
  })();
  const [isLoggedIn, setIsLoggedIn] = useState(!!initialPwd);
  const [adminPwd, setAdminPwd] = useState(initialPwd);
  const [activeTab, setActiveTab] = useState('artists');

  // Allow sub-components to switch tabs via a custom event (e.g. from empty-state CTAs).
  useEffect(() => {
    const handler = (e) => { if (e.detail) setActiveTab(e.detail); };
    window.addEventListener('switch-admin-tab', handler);
    return () => window.removeEventListener('switch-admin-tab', handler);
  }, []);

  const handleLogin = async (pwd, setError) => {
    try {
      const res = await fetch(`${API}/artists`);
      if (res.ok) {
        // Mirror the password into localStorage so useInlineAdmin (Community,
        // Playlist, Gallery) picks it up too — no more "log in twice".
        try { localStorage.setItem(ADMIN_LS_KEY, pwd); } catch (_e) { /* noop */ }
        setAdminPwd(pwd);
        setIsLoggedIn(true);
      }
    } catch {
      setError('Erreur de connexion');
    }
  };

  const handleLogout = () => {
    try { localStorage.removeItem(ADMIN_LS_KEY); } catch (_e) { /* noop */ }
    setAdminPwd('');
    setIsLoggedIn(false);
    onBack?.();
  };

  if (!isLoggedIn) return <AdminLogin onLogin={handleLogin} />;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isProd = hostname.includes('kazajahstudio.com');
  const buildLabel = '2026-06-09 · Album Packs + Share Artist';

  const tabs = [
    { id: 'artists', label: 'Artistes', icon: Users },
    { id: 'events', label: 'Evenements', icon: Calendar },
    { id: 'tracks', label: 'Tracks', icon: Music },
    { id: 'community', label: 'Community', icon: Inbox },
    { id: 'services', label: 'Services Pro', icon: Briefcase },
    { id: 'ai-credits', label: 'AI Credits', icon: Sparkles },
    { id: 'beat-samples', label: 'Beat Samples', icon: Keyboard },
    { id: 'album-packs', label: 'Album Packs', icon: Disc3 },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
    { id: 'backup', label: 'Backup', icon: Download },
    { id: 'site', label: 'Site & Textes', icon: Edit3 },
  ];

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-8" data-testid="admin-dashboard">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-black text-white">Admin KAZAJAH</h1>
            <p className="text-zinc-500 text-sm">Gestion du site</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onBack} data-testid="admin-back-button"
              className="px-3 py-1.5 text-zinc-400 hover:text-white flex items-center gap-1.5 text-sm">
              <ArrowRight className="rotate-180" size={16} /> Retour au site
            </button>
            <button onClick={handleLogout} data-testid="admin-logout-button"
              className="px-3 py-1.5 rounded-lg bg-zinc-900 hover:bg-red-500/20 text-zinc-400 hover:text-red-300 border border-zinc-800 hover:border-red-500/40 flex items-center gap-1.5 text-xs font-bold transition-colors">
              <Lock size={12} /> Se deconnecter
            </button>
          </div>
        </div>

        {/* Environment & build banner */}
        <div
          data-testid="admin-env-banner"
          className={`mb-6 rounded-xl border px-4 py-3 text-xs flex items-center gap-3 flex-wrap ${
            isProd
              ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-200'
              : 'border-amber-500/30 bg-amber-500/5 text-amber-200'
          }`}>
          <span className={`px-2 py-0.5 rounded font-black ${isProd ? 'bg-emerald-500 text-black' : 'bg-amber-500 text-black'}`}>
            {isProd ? 'PROD' : 'PREVIEW'}
          </span>
          <span className="font-mono opacity-70">{hostname}</span>
          <span className="opacity-50">•</span>
          <span>Build : <b>{buildLabel}</b></span>
          {!isProd && (
            <span className="ml-auto opacity-90">
              Modifications visibles uniquement ici tant que tu n&apos;as pas clique sur <b>Deploy</b>.
            </span>
          )}
          {isProd && (
            <span className="ml-auto opacity-90">
              Si une fonctionnalite manque ici, ouvre le preview Emergent et clique <b>Deploy</b>.
            </span>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-zinc-800 pb-4">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${
                activeTab === tab.id
                  ? 'bg-yellow-500 text-black'
                  : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
              }`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            {activeTab === 'artists' && <ArtistsTab adminPwd={adminPwd} />}
            {activeTab === 'events' && <EventsTab adminPwd={adminPwd} />}
            {activeTab === 'tracks' && <TracksTab adminPwd={adminPwd} />}
            {activeTab === 'community' && <CommunityTab adminPwd={adminPwd} />}
            {activeTab === 'services' && <ServicesProTab adminPwd={adminPwd} />}
            {activeTab === 'ai-credits' && <AICreditsTab adminPwd={adminPwd} />}
            {activeTab === 'beat-samples' && <BeatSamplesTab adminPwd={adminPwd} />}
            {activeTab === 'album-packs' && <AlbumPacksTab adminPwd={adminPwd} />}
            {activeTab === 'newsletter' && <NewsletterTab adminPwd={adminPwd} />}
            {activeTab === 'backup' && <BackupTab adminPwd={adminPwd} />}
            {activeTab === 'site' && <SiteTab adminPwd={adminPwd} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
